import { AxiosError } from 'axios';
import { useMutation } from 'react-query';

// utils
import { ACCESS_TOKEN, REFRESH_TOKEN, setCookie, setSecureCookie } from '@utils/cookie';
import { Toast } from '@components/_atoms/Toast';

// apis
import {
  validEmailApi,
  loginApi,
  resetPasswordApi,
  sendPasswordFindEmailApi,
  validCompanyRegistrationNumberApi,
  signUpApi,
  signUpByInvitationApi,
  verifyEmailApi,
  resendEmailApi,
} from '@apis/auth';

// types
import {
  AuthCrnPostCrnValidationError,
  AuthCrnPostCrnValidationPayload,
  AuthEmailGetEmailVerifyError,
  AuthEmailPostEmailResendError,
  AuthEmailPostEmailValidationError,
  AuthPasswordPostPasswordFindError,
  AuthPasswordPostPasswordResetError,
  AuthPasswordPostPasswordResetPayload,
  AuthPostLoginError,
  AuthPostLoginPayload,
  AuthPostRegisterError,
  AuthPostRegisterInvitationPayload,
  AuthPostRegisterPayload,
} from 'models/swagger/data-contracts';
import {
  AuthEmailPostEmailCheckPayload,
  AuthPasswordPostPasswordResetEmailPayload,
  resendEmailPayload,
  verifyEmailPayload,
} from 'models/Supplement';
import { HOUR, MINUTE, MONTH } from '@constants/time';

/**
 * 로그인 Fetch
 * @function useLoginFetch
 */
export const useLoginFetch = () => {
  const currentDomain = window.location.hostname;

  const {
    mutate: loginFetchMutate,
    status: loginFetchStatus,
    isLoading: loginFetchIsLoading,
  } = useMutation(
    ['login'],
    ({ email, password }: AuthPostLoginPayload) => loginApi({ email: email, password: password }),
    {
      onSuccess: (res) => {
        setCookie(ACCESS_TOKEN, res.data.data?.access_token, { maxAge: 1 * HOUR, domain: currentDomain, path: '/' });
        setCookie(REFRESH_TOKEN, res.data.data?.refresh_token, { maxAge: 3 * MONTH, domain: currentDomain, path: '/' });
      },
      onError: (err: AxiosError<AuthPostLoginError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    loginFetchIsLoading,
    loginFetchMutate,
    loginFetchStatus,
  };
};

/**
 *  비밀번호 초기화 인증 메일 전송 요청 Fetch
 *  @function useSendPasswordFindEmailFetch
 */
export const useSendPasswordFindEmailFetch = () => {
  const {
    mutate: sendPasswordFindEmailFetchMutate,
    status: sendPasswordFindEmailFetchStatus,
    isLoading: sendPasswordFindEmailFetchIsLoading,
  } = useMutation(
    ['sendPasswordFindEmail'],
    ({ email }: AuthPasswordPostPasswordResetEmailPayload) => sendPasswordFindEmailApi({ email }),
    {
      onError: (err: AxiosError<AuthPasswordPostPasswordFindError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    sendPasswordFindEmailFetchMutate,
    sendPasswordFindEmailFetchStatus,
    sendPasswordFindEmailFetchIsLoading,
  };
};

/**
 *  비밀번호 초기화 인증 및 변경 Fetch
 *  @function useResetPasswordFetch
 */
export const useResetPasswordFetch = () => {
  const {
    mutate: resetPasswordFetchMutate,
    status: resetPasswordFetchStatus,
    isLoading: resetPasswordFetchIsLoading,
  } = useMutation(
    ['resetPassword'],
    ({ t, password }: AuthPasswordPostPasswordResetPayload) => resetPasswordApi({ t, password }),
    {
      onError: (err: AxiosError<AuthPasswordPostPasswordResetError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    resetPasswordFetchMutate,
    resetPasswordFetchStatus,
    resetPasswordFetchIsLoading,
  };
};

/**
 *  이메일 중복 및 형식 검사 Fetch
 *  @function useValidEmailFetch
 */
export const useValidEmailFetch = () => {
  const {
    mutate: validEmailFetchMutate,
    status: validEmailFetchStatus,
    isLoading: validEmailFetchIsLoading,
  } = useMutation(['validEmail'], ({ email }: AuthEmailPostEmailCheckPayload) => validEmailApi({ email }), {
    onError: (err: AxiosError<AuthEmailPostEmailValidationError>) => {
      if (err.response?.data?.message) {
        Toast.error(err.response.data.message.text);
      }
    },
  });

  return {
    validEmailFetchMutate,
    validEmailFetchStatus,
    validEmailFetchIsLoading,
  };
};

/**
 *  사업자 등록번호 검증 및 확인 Fetch
 *  @function useValidCompanyRegistrationNumberFetch
 */
export const useValidCompanyRegistrationNumberFetch = () => {
  const {
    mutate: validCompanyRegistrationNumberFetchMutate,
    status: validCompanyRegistrationNumberFetchStatus,
    isLoading: validCompanyRegistrationNumberFetchIsLoading,
  } = useMutation(
    ['validCompanyRegistrationNumber'],
    ({ company_registration_number }: AuthCrnPostCrnValidationPayload) =>
      validCompanyRegistrationNumberApi({ company_registration_number }),
    {
      onError: (err: AxiosError<AuthCrnPostCrnValidationError>) => {
        if (err.response?.data?.message) {
          // Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    validCompanyRegistrationNumberFetchMutate,
    validCompanyRegistrationNumberFetchStatus,
    validCompanyRegistrationNumberFetchIsLoading,
  };
};

/**
 *  회원가입 Fetch
 *  @function useSignUpFetch
 */
export const useSignUpFetch = () => {
  const {
    mutate: signUpFetchMutate,
    status: signUpFetchStatus,
    isLoading: signUpFetchIsLoading,
  } = useMutation(
    ['signUp'],
    ({
      email,
      password,
      company_registration_number,
      company_name,
      company_address,
      company_ceo,
      name,
      phone_code,
      phone,
      company_department,
      company_title,
      marketing_approval,
      company_registration_number_file,
      company_registration_number_file_name,
    }: AuthPostRegisterPayload) =>
      signUpApi({
        email,
        password,
        company_registration_number,
        company_name,
        company_address,
        company_ceo,
        name,
        phone_code,
        phone,
        company_department,
        company_title,
        marketing_approval,
        company_registration_number_file,
        company_registration_number_file_name,
      }),
    {
      onError: (err: AxiosError<AuthPostRegisterError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    signUpFetchMutate,
    signUpFetchStatus,
    signUpFetchIsLoading,
  };
};

/**
 *  초대를 통한 회원가입 Fetch
 *  @function useSignUpByInvitationFetch
 */
export const useSignUpByInvitationFetch = () => {
  const {
    mutate: signUpByInvitationFetchMutate,
    status: signUpByInvitationFetchStatus,
    isLoading: signUpByInvitationFetchIsLoading,
  } = useMutation(
    ['signUpByInvitation'],
    ({
      token,
      email,
      password,
      name,
      phone_code,
      phone,
      company_department,
      company_title,
      marketing_approval,
    }: AuthPostRegisterInvitationPayload) =>
      signUpByInvitationApi({
        token,
        email,
        password,
        name,
        phone_code,
        phone,
        company_department,
        company_title,
        marketing_approval,
      }),
    {
      onError: (err: AxiosError<AuthPostRegisterError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    signUpByInvitationFetchMutate,
    signUpByInvitationFetchStatus,
    signUpByInvitationFetchIsLoading,
  };
};

/**
 *  이메일 인증 Fetch
 *  @function useVerifyEmailFetch
 */
export const useVerifyEmailFetch = () => {
  const {
    mutate: verifyEmailFetchMutate,
    status: verifyEmailFetchStatus,
    isLoading: verifyEmailFetchIsLoading,
    data: verifyEmailFetchData,
  } = useMutation(['verifyEmail'], ({ t }: verifyEmailPayload) => verifyEmailApi({ t }), {
    onError: (err: AxiosError<AuthEmailGetEmailVerifyError>) => {
      if (err.response?.data?.message) {
        Toast.error(err.response.data.message.text);
      }
    },
  });

  return {
    verifyEmailFetchMutate,
    verifyEmailFetchStatus,
    verifyEmailFetchIsLoading,
    verifyEmailFetchData,
  };
};

/**
 *  이메일 재전송 Fetch
 *  @function useResendEmailFetch
 */
export const useResendEmailFetch = () => {
  const { mutate: resendEmailFetchMutate } = useMutation(
    ['resendEmail'],
    ({ email }: resendEmailPayload) => resendEmailApi({ email }),
    {
      onError: (err: AxiosError<AuthEmailPostEmailResendError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    resendEmailFetchMutate,
  };
};
